<script lang="ts">
import {
  defineComponent,
  toRef,
  reactive,
  onMounted,
} from 'vue'
import LayoutSolutionContentsShowPage from '@/components/solutions/LayoutSolutionContentsShowPage.vue'
import ExternalHtmlWrapper from '@/components/_core/ExternalHtmlWrapper.vue'
import SplitComponent from './SplitComponent.vue'
import stateInitializer from '@/models/stateInitializer'
import userCareer from '@/models/userCareer'
import leadGen from '@/models/leadGen'
import customFields from '@/models/customFields'
import hearingFieldsOperator from '@/models/hearingFieldsOperator'

export default defineComponent({
  name: 'DocumentMain',

  components: {
    LayoutSolutionContentsShowPage,
    ExternalHtmlWrapper,
    SplitComponent,
  },

  emits: ['init:logoColor'],

  setup(_, { emit }) {
    interface State {
      meta: {
        corporateBrandColor: {
          primary: string,
          secondary: string,
        },
      },
      form: Form,
      input: {
        userInfo: {
          lastName: InputText,
          firstName: InputText,
          lastNameKana: InputText,
          firstNameKana: InputText,
          companyName: InputText,
          industry: InputText,
          scale: InputText,
          listingStatus: InputText,
          profession: InputText,
          professionDomain: InputMultiSelect,
          positionLevel: InputText,
          departmentAndPosition: InputText,
          workMailAddress: InputText,
          phoneNumber: InputText,
          corporateZipcode: InputText,
          corporateAddress1: InputText,
          corporateAddress2: InputText,
          corporateNumber: InputText,
          corporateEmployeeNum: InputNumber,
        },

        optionalNewsletterSettings: {
          mailStopFlag: InputBoolean,
          mailNewsStopFlag: InputBoolean,
          mailExpoStopFlag: InputBoolean,
          mailAiStopFlag: InputBoolean,
        },

        customFields: Inputs,

        hearingFields: Inputs,

        /**
         * プロフィール情報がすべて正常な値で埋まっているかどうかのサーバ側の保有値
         */
        isFullFilled: boolean,
      },
      document: SolutionDocumentItem,

      /**
       * Custom Field のサーバからの情報受取用
       * ※ Custom Field の入力値格納用は State の input オブジェクトに別途用意
       */
      customFieldConfigItems?: CustomFieldConfig[],

      /**
       * ヒアリング項目のサーバからの情報受取用
       * ※ ヒアリング項目の入力値格納用は State の input オブジェクトに別途用意
       */
      hearingFieldConfig?: HearingFieldConfig,

      splitTest: SplitTest,

      /**
       * プレビュー表示かどうか（サーバの previews/documents#show から渡る）
       */
      preview: boolean,
    }
    const state: State = reactive({
      meta: {
        corporateBrandColor: {
          primary: '',
          secondary: '',
        },
      },
      form: {
        name: '',
        action: '',
        method: 'post',
      },
      input: {
        userInfo: {
          lastName: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          firstName: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          lastNameKana: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
              kana: true,
            },
          },
          firstNameKana: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
              kana: true,
            },
          },
          companyName: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          industry: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          scale: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          listingStatus: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          profession: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          professionDomain: {
            value: [],
            name: '',
            validationScheme: {},
          },
          positionLevel: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          departmentAndPosition: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          workMailAddress: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
              email: true,
              mailDomain: true,
            },
          },
          phoneNumber: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
              phoneNumber: true,
            },
          },
          corporateZipcode: {
            value: '',
            name: '',
            validationScheme: {},
          },
          corporateAddress1: {
            value: '',
            name: '',
            validationScheme: {},
          },
          corporateAddress2: {
            value: '',
            name: '',
            validationScheme: {},
          },
          corporateNumber: {
            value: '',
            name: '',
            validationScheme: {},
          },
          corporateEmployeeNum: {
            value: undefined,
            name: '',
            validationScheme: {},
          },
        },
        optionalNewsletterSettings: {
          mailStopFlag: {
            value: false,
            name: '',
            validationScheme: {},
          },
          mailNewsStopFlag: {
            value: false,
            name: '',
            validationScheme: {},
          },
          mailExpoStopFlag: {
            value: false,
            name: '',
            validationScheme: {},
          },
          mailAiStopFlag: {
            value: false,
            name: '',
            validationScheme: {},
          },
        },
        customFields: {},
        hearingFields: {},
        isFullFilled: false,
      },

      document: {
        id: undefined,
        title: '',
        thumbImageUrl: '',
        introduction: '',
        solution: {
          id: undefined,
          title: '',
          logoImageUrl: '',
          categoryIds: [],
          company: {
            name: '',
          },
          privacyPolicy: '',
        },
      },
      customFieldConfigItems: [],
      hearingFieldConfig: {
        nameSuffix: {
          id: '',
          hearingFieldId: '',
          value: '',
          secondaryValue: '',
        },
        sections: [],
      },

      splitTest: {
        name: '',
        pattern: '',
      },

      preview: false,
    })

    stateInitializer(state, 'main')

    const { isShowProfessionDomain } = userCareer(toRef(state.input, 'userInfo'))

    const {
      selectCompanyLeadInfo,
      inputCompanyName,
    } = leadGen(state.input.userInfo)

    customFields(state.input.customFields, state.customFieldConfigItems)

    const {
      // methods
      initInputHearingFields,
    } = hearingFieldsOperator(state.hearingFieldConfig, state.input.hearingFields)

    onMounted(() => {
      // 「担当領域」を表示する場合は、「担当領域」のバリデーションを必須にする
      if (isShowProfessionDomain.value) {
        Object.assign(
          state.input.userInfo.professionDomain,
          { validationScheme: { required: true } },
        )
      }

      // ブランドカラーが設定されている場合は BizHint ロゴをモノトーンにする
      if (state.meta.corporateBrandColor.primary && state.meta.corporateBrandColor.secondary) {
        emit('init:logoColor', 'gray')
      }
    })

    /**
     * ヒアリング項目の「その他」に該当する項目が選択された際の処理
     *
     * @param name 対応する追加設問の name 属性値
     * @param value 対応する追加設問の入力要素オブジェクト
     * @description 引数は emit で通知される値を暗黙的に受け取る
     */
    const onSelectInHearingMultiSelectorPrimary = (name: string, value: Input) => {
      if (state.input.hearingFields && !state.input.hearingFields[name]) {
        Object.assign(state.input.hearingFields, { [name]: value })
      }
    }
    const onCloseHearingMultiSelectorSecondary = (name: string) => {
      if (state.input.hearingFields && state.input.hearingFields[name]) {
        delete state.input.hearingFields[name]
      }
    }

    /**
     * 「担当領域」の入力が必要な「職種」が選択された場合の処理
     */
    const onSelectedProfessionOptionLinkedToDomain = () => {
      Object.assign(
        state.input.userInfo.professionDomain,
        { validationScheme: { required: true } },
      )
    }

    /**
     * 「担当領域」の入力が不要な「職種」が選択された場合の処理
     */
    const onUnselectedProfessionOptionLinkedToDomain = () => {
      Object.assign(
        state.input.userInfo.professionDomain,
        { validationScheme: { required: false } },
      )
    }

    initInputHearingFields()

    return {
      // data
      state,

      // methods
      selectCompanyLeadInfo,
      inputCompanyName,
      onSelectInHearingMultiSelectorPrimary,
      onCloseHearingMultiSelectorSecondary,
      onSelectedProfessionOptionLinkedToDomain,
      onUnselectedProfessionOptionLinkedToDomain,
    }
  },
})
</script>

<template>
  <LayoutSolutionContentsShowPage
    v-if="state.document.id"
    :contents="state.document"
    :form="state.form"
    :input-user-info="state.input.userInfo"
    :input-optional-newsletter-info="state.input.optionalNewsletterSettings"
    :input-custom-fields="state.input.customFields"
    :custom-field-config-items="state.customFieldConfigItems"
    :input-hearing-fields="state.input.hearingFields"
    :hearing-field-config="state.hearingFieldConfig"
    :is-full-filled="state.input.isFullFilled"
    :corporate-brand-color="state.meta.corporateBrandColor"
    :is-preview="state.preview"
    :submit-button-label="'資料を見る'"
    @update:last-name="state.input.userInfo.lastName.value = $event"
    @update:first-name="state.input.userInfo.firstName.value = $event"
    @update:last-name-kana="state.input.userInfo.lastNameKana.value = $event"
    @update:first-name-kana="state.input.userInfo.firstNameKana.value = $event"
    @update:company-name="inputCompanyName($event)"
    @update:industry="state.input.userInfo.industry.value = $event"
    @update:scale="state.input.userInfo.scale.value = $event"
    @update:listing-status="state.input.userInfo.listingStatus.value = $event"
    @update:profession="state.input.userInfo.profession.value = $event"
    @update:profession-domain="state.input.userInfo.professionDomain.value = $event"
    @open:profession-domain="onSelectedProfessionOptionLinkedToDomain"
    @close:profession-domain="onUnselectedProfessionOptionLinkedToDomain"
    @update:position-level="state.input.userInfo.positionLevel.value = $event"
    @update:department-and-position="state.input.userInfo.departmentAndPosition.value = $event"
    @update:work-mail-address="state.input.userInfo.workMailAddress.value = $event"
    @update:phone-number="state.input.userInfo.phoneNumber.value = $event"
    @update:corporate-zipcode="state.input.userInfo.corporateZipcode.value = $event"
    @update:corporate-address1="state.input.userInfo.corporateAddress1.value = $event"
    @update:corporate-address2="state.input.userInfo.corporateAddress2.value = $event"
    @select:company-lead-info="selectCompanyLeadInfo($event)"
    @update:custom-fields="Object.assign(state.input.customFields, $event)"
    @update:hearing-fields="Object.assign(state.input.hearingFields, $event)"
    @update:mail-stop-flag="state.input.optionalNewsletterSettings.mailStopFlag.value = $event"
    @update:mail-news-stop-flag="state.input.optionalNewsletterSettings.mailNewsStopFlag.value = $event"
    @update:mail-expo-stop-flag="state.input.optionalNewsletterSettings.mailExpoStopFlag.value = $event"
    @update:mail-ai-stop-flag="state.input.optionalNewsletterSettings.mailAiStopFlag.value = $event"
    @reset:corporate-zipcode="state.input.userInfo.corporateZipcode.value = $event"
    @select:hearing-multi-select-primary="onSelectInHearingMultiSelectorPrimary"
    @close:hearing-multi-select-secondary="onCloseHearingMultiSelectorSecondary($event)">
    <template
      v-if="state.splitTest?.pattern"
      #thumbImage>
      <SplitComponent
        :pattern="state.splitTest?.pattern"
        field="thumbImage"
        :document-id="state.document.id">
        <img
          v-if="state.document.thumbImageUrl"
          class="img"
          :src="state.document.thumbImageUrl"
          fetchpriority="high"
          alt="">
      </SplitComponent>
    </template>
    <template #body>
      <SplitComponent
        :pattern="state.splitTest?.pattern"
        field="introduction"
        :document-id="state.document.id">
        <ExternalHtmlWrapper
          v-if="state.document.introduction"
          :contents-html="state.document.introduction"
          :color-palette="state.meta.corporateBrandColor" />
      </SplitComponent>
    </template>
    <template #headingLabel>
      <SplitComponent
        :pattern="state.splitTest?.pattern"
        field="profileHeadingLabel"
        :document-id="state.document.id">
        下記の情報で間違いないかご確認ください
      </SplitComponent>
    </template>
  </LayoutSolutionContentsShowPage>
</template>
